import type { CartItem } from '@/js/types/cart.ts';
import { GOALS, reachGoal } from '@/js/goal.ts';
import { getEcommerceContainer } from '@/js/metrika.ts';

const CURRENCY_CODE = 'RUB';
const DETAIL_SELECTOR = '.js-ecommerce-detail';
const PURCHASE_SELECTOR = '.js-ecommerce-purchase';

export type EcommerceProduct = {
  id: string
  name: string
  price?: number
  brand?: string
  category?: string
  quantity?: number
};

export type PurchaseActionField = {
  id: string
  revenue?: number
  shipping?: number
};

type PurchaseData = {
  actionField: PurchaseActionField
  products?: EcommerceProduct[]
};

function pushEcommerce(payload: Record<string, unknown>) {
  getEcommerceContainer().push({
    ecommerce: {
      currencyCode: CURRENCY_CODE,
      ...payload,
    },
  });
}

export function toEcommerceProduct(item: CartItem, quantity: number): EcommerceProduct {
  // API отдаёт цену строкой (decimal в JSON), а Метрика ждёт число —
  // иначе выручка в отчёте по электронной коммерции не считается.
  const price = Number(item.price);

  return {
    id: String(item.productId),
    name: item.productName,
    price: Number.isFinite(price) ? price : undefined,
    quantity,
  };
}

export function ecommerceDetail(products: EcommerceProduct[]) {
  if (products.length === 0) {
    return;
  }
  pushEcommerce({ detail: { products } });
}

export function ecommerceAdd(products: EcommerceProduct[]) {
  if (products.length === 0) {
    return;
  }
  pushEcommerce({ add: { products } });
}

export function ecommerceRemove(products: EcommerceProduct[]) {
  if (products.length === 0) {
    return;
  }
  pushEcommerce({ remove: { products } });
}

export function ecommercePurchase(actionField: PurchaseActionField, products: EcommerceProduct[]) {
  pushEcommerce({ purchase: { actionField, products } });
}

function readJsonScript<T>(selector: string): T | null {
  const element = document.querySelector<HTMLScriptElement>(selector);
  const content = element?.textContent ?? '';
  if (content === '') {
    return null;
  }
  try {
    return JSON.parse(content) as T;
  }
  catch (e) {
    console.error(e);
    return null;
  }
}

/** Просмотр карточки товара — данные отдаёт шаблон страницы товара. */
export function initEcommerceDetail() {
  const product = readJsonScript<EcommerceProduct>(DETAIL_SELECTOR);
  if (product) {
    ecommerceDetail([product]);
  }
}

/**
 * Покупка на странице успешного заказа. Данные приходят из flash-сессии,
 * поэтому при перезагрузке страницы покупка не отправится повторно.
 */
export function initEcommercePurchase() {
  const purchase = readJsonScript<PurchaseData>(PURCHASE_SELECTOR);
  if (!purchase) {
    return;
  }
  ecommercePurchase(purchase.actionField, purchase.products ?? []);
  reachGoal(GOALS.ORDER_SUCCESS);
}
